import React, { useEffect, useMemo, useState } from 'react';
import {
  Plus, Edit2, Trash2, X, Save, ArrowLeft, Calendar,
  CheckCircle2, Circle, ChevronRight, Users, Paperclip,
} from 'lucide-react';
import { useApi } from '../auth/useApi';
import { TaskAttachments } from '../components/TaskAttachments';
import { ProjectNotes } from '../components/ProjectNotes';

const C = {
  primary: 'var(--c-primary)',
  secondary: 'var(--c-secondary)',
  bg: 'var(--c-bg)',
  surface: 'var(--c-surface)',
  border: 'var(--c-border)',
  text: 'var(--c-text)',
  textMuted: 'var(--c-textMuted)',
  ok: 'var(--c-ok)',
  warn: 'var(--c-warn)',
  danger: 'var(--c-danger)',
};

const STATUS_COLORS: Record<string, string> = {
  'planning':    C.secondary,
  'in-progress': C.primary,
  'on-hold':     C.warn,
  'completed':   C.ok,
};

const STATUSES = ['planning', 'in-progress', 'on-hold', 'completed'];

interface Me {
  userId: number;
  role: 'admin' | 'tech' | 'user' | 'viewer' | 'agent';
}

interface Project {
  ProjectId: number;
  Name: string;
  Description: string | null;
  OwnerUserId: number | null;
  OwnerName: string | null;
  Status: string;
  Progress: number;
  StartDate: string | null;
  DueDate: string | null;
  TeamJson: string | null;
  TaskCount?: number;
  TasksCompleted?: number;
  CreatedAt: string;
}

interface Task {
  TaskId: number;
  ProjectId: number;
  Label: string;
  Completed: boolean;
  SortOrder: number;
  DueDate: string | null;
  AssignedUserId: number | null;
  AssigneeName: string | null;
}

interface User { UserId: number; DisplayName: string; Email: string; Role: string; }

// ============================================================================
// Projects Page
// ============================================================================
export const Projects: React.FC<{ me: Me }> = ({ me }) => {
  const api = useApi();
  const [projects, setProjects] = useState<Project[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('active');

  const canEdit = me.role === 'admin' || me.role === 'tech';
  const canDelete = me.role === 'admin';

  const load = async () => {
    setLoading(true);
    try {
      const [p, u] = await Promise.all([
        api.get<Project[]>('/api/projects'),
        api.get<User[]>('/api/users').catch(() => [] as User[]),
      ]);
      setProjects(Array.isArray(p) ? p : []);
      setUsers(Array.isArray(u) ? u : []);
      setError(null);
    } catch (e: any) { setError(e?.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const filtered = useMemo(() => {
    if (statusFilter === 'all') return projects;
    if (statusFilter === 'active') return projects.filter(p => p.Status !== 'completed');
    return projects.filter(p => p.Status === statusFilter);
  }, [projects, statusFilter]);

  if (loading) return <div style={{ padding: 40, color: C.textMuted }}>Loading projects…</div>;
  if (error) return <div style={{ padding: 40, color: C.danger }}>Failed to load: {error}</div>;

  // Detail view
  if (selectedId !== null) {
    return (
      <ProjectDetail
        projectId={selectedId}
        users={users}
        canEdit={canEdit}
        canDelete={canDelete}
        onBack={() => { setSelectedId(null); load(); }}
      />
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 18 }}>
        <div>
          <h1 style={{ color: C.primary, fontSize: 26, margin: 0 }}>Projects</h1>
          <p style={{ color: C.textMuted, margin: '4px 0 0' }}>
            {filtered.length} of {projects.length} {projects.length === 1 ? 'project' : 'projects'}
          </p>
        </div>
        {canEdit && (
          <button onClick={() => setCreating(true)} style={primaryBtnSty()}>
            <Plus size={14} /> New Project
          </button>
        )}
      </div>

      {/* Filter pills */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
        {[
          { key: 'active',      label: 'Active',      count: projects.filter(p => p.Status !== 'completed').length },
          { key: 'in-progress', label: 'In Progress', count: projects.filter(p => p.Status === 'in-progress').length },
          { key: 'planning',    label: 'Planning',    count: projects.filter(p => p.Status === 'planning').length },
          { key: 'on-hold',     label: 'On Hold',     count: projects.filter(p => p.Status === 'on-hold').length },
          { key: 'completed',   label: 'Completed',   count: projects.filter(p => p.Status === 'completed').length },
          { key: 'all',         label: 'All',         count: projects.length },
        ].map(opt => (
          <button key={opt.key} onClick={() => setStatusFilter(opt.key)} style={{
            background: statusFilter === opt.key ? C.primary : C.surface,
            color: statusFilter === opt.key ? '#fff' : C.text,
            border: `1px solid ${statusFilter === opt.key ? C.primary : C.border}`,
            padding: '6px 14px', borderRadius: 16, fontSize: 12,
            cursor: 'pointer', fontWeight: 500,
          }}>
            {opt.label} <span style={{ marginLeft: 4, opacity: 0.7, fontSize: 11 }}>({opt.count})</span>
          </button>
        ))}
      </div>

      {/* Card grid */}
      {filtered.length === 0 ? (
        <div style={{
          padding: 60, textAlign: 'center', color: C.textMuted,
          background: C.surface, border: `1px dashed ${C.border}`, borderRadius: 8,
        }}>
          No projects match the current filter.
        </div>
      ) : (
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 14,
        }}>
          {filtered.map(p => (
            <ProjectCard key={p.ProjectId} project={p} onClick={() => setSelectedId(p.ProjectId)} />
          ))}
        </div>
      )}

      {creating && (
        <ProjectModal
          project={null}
          users={users}
          onSave={async (body) => {
            try {
              await api.post('/api/projects', body);
              setCreating(false);
              await load();
            } catch (e: any) { alert(`Failed: ${e?.message}`); }
          }}
          onClose={() => setCreating(false)}
        />
      )}
    </div>
  );
};

// ============================================================================
// Project Card
// ============================================================================
const ProjectCard: React.FC<{ project: Project; onClick: () => void }> = ({ project, onClick }) => {
  const due = project.DueDate ? new Date(project.DueDate) : null;
  const overdue = due && due.getTime() < Date.now() && project.Status !== 'completed';
  const dueSoon = due && !overdue && (due.getTime() - Date.now()) < 7 * 86400000;
  const taskProgress = project.TaskCount && project.TaskCount > 0
    ? Math.round(((project.TasksCompleted ?? 0) / project.TaskCount) * 100)
    : project.Progress;

  return (
    <div
      onClick={onClick}
      style={{
        background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8,
        padding: 18, cursor: 'pointer', transition: 'border-color 0.15s, transform 0.1s',
        display: 'flex', flexDirection: 'column', gap: 10,
      }}
      onMouseEnter={e => (e.currentTarget.style.borderColor = C.primary)}
      onMouseLeave={e => (e.currentTarget.style.borderColor = C.border)}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Badge color={STATUS_COLORS[project.Status] ?? C.textMuted}>{project.Status}</Badge>
        <ChevronRight size={14} color={C.textMuted} />
      </div>

      <h3 style={{ margin: 0, fontSize: 16, color: C.text, fontWeight: 600 }}>{project.Name}</h3>

      {project.Description && (
        <div style={{
          fontSize: 12, color: C.textMuted, lineHeight: 1.4,
          overflow: 'hidden', textOverflow: 'ellipsis',
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
        }}>{project.Description}</div>
      )}

      {/* Progress */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: C.textMuted, marginBottom: 4 }}>
          <span>{(project.TasksCompleted ?? 0)}/{project.TaskCount ?? 0} tasks</span>
          <span style={{ color: C.text, fontWeight: 600 }}>{taskProgress}%</span>
        </div>
        <div style={{ height: 6, background: C.bg, borderRadius: 3, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${taskProgress}%`, background: STATUS_COLORS[project.Status] ?? C.primary, transition: 'width 0.3s' }} />
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: C.textMuted, alignItems: 'center' }}>
        <span>{project.OwnerName ?? 'No owner'}</span>
        {due && (
          <span style={{
            color: overdue ? C.danger : dueSoon ? C.warn : C.textMuted,
            display: 'inline-flex', alignItems: 'center', gap: 4,
            fontWeight: overdue || dueSoon ? 600 : 400,
          }}>
            <Calendar size={11} />
            {overdue ? 'Overdue' : due.toLocaleDateString()}
          </span>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// Project Detail
// ============================================================================
const ProjectDetail: React.FC<{
  projectId: number;
  users: User[];
  canEdit: boolean;
  canDelete: boolean;
  onBack: () => void;
}> = ({ projectId, users, canEdit, canDelete, onBack }) => {
  const api = useApi();
  const [project, setProject] = useState<Project | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [team, setTeam] = useState<User[]>([]);
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newTaskLabel, setNewTaskLabel] = useState('');
  const [expandedTask, setExpandedTask] = useState<number | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await api.get<{ project: Project; tasks: Task[]; team: User[] }>(`/api/projects/${projectId}`);
      setProject(data.project);
      setTasks(Array.isArray(data.tasks) ? data.tasks.sort((a, b) => a.SortOrder - b.SortOrder) : []);
      setTeam(Array.isArray(data.team) ? data.team : []);
      setError(null);
    } catch (e: any) { setError(e?.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

  const toggleTask = async (task: Task) => {
    try {
      await api.patch(`/api/projects/tasks/${task.TaskId}`, { completed: !task.Completed });
      await load();
    } catch (e: any) { alert(`Failed: ${e?.message}`); }
  };

  const updateTask = async (taskId: number, patch: { dueDate?: string | null; assignedUserId?: number | null }) => {
    try {
      await api.patch(`/api/projects/tasks/${taskId}`, patch);
      await load();
    } catch (e: any) { alert(`Failed: ${e?.message}`); }
  };

  const assignableUsers = users.filter(u => u.Role === 'admin' || u.Role === 'tech');

  const addTask = async () => {
    if (!newTaskLabel.trim()) return;
    try {
      await api.post(`/api/projects/${projectId}/tasks`, { label: newTaskLabel.trim() });
      setNewTaskLabel('');
      await load();
    } catch (e: any) { alert(`Failed: ${e?.message}`); }
  };

  const deleteTask = async (taskId: number) => {
    if (!window.confirm('Delete this task?')) return;
    try {
      await api.del(`/api/projects/tasks/${taskId}`);
      await load();
    } catch (e: any) { alert(`Failed: ${e?.message}`); }
  };

  const deleteProject = async () => {
    if (!window.confirm(`Delete project "${project?.Name}"? This will also delete all tasks. This cannot be undone.`)) return;
    try {
      await api.del(`/api/projects/${projectId}`);
      onBack();
    } catch (e: any) { alert(`Failed: ${e?.message}`); }
  };

  if (loading) return <div style={{ padding: 40, color: C.textMuted }}>Loading…</div>;
  if (error || !project) return (
    <div style={{ padding: 40, color: C.danger }}>
      <button onClick={onBack} style={backBtnSty()}><ArrowLeft size={14} /> Back</button>
      <div>Failed to load: {error}</div>
    </div>
  );

  const taskProgress = tasks.length > 0
    ? Math.round((tasks.filter(t => t.Completed).length / tasks.length) * 100)
    : project.Progress;
  const due = project.DueDate ? new Date(project.DueDate) : null;
  const overdue = due && due.getTime() < Date.now() && project.Status !== 'completed';

  return (
    <div>
      <button onClick={onBack} style={backBtnSty()}><ArrowLeft size={14} /> Back to projects</button>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, marginBottom: 20 }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
            <Badge color={STATUS_COLORS[project.Status] ?? C.textMuted}>{project.Status}</Badge>
            {overdue && <Badge color={C.danger}>Overdue</Badge>}
          </div>
          <h1 style={{ color: C.text, fontSize: 22, margin: '0 0 4px', fontWeight: 600 }}>{project.Name}</h1>
          <div style={{ fontSize: 12, color: C.textMuted }}>
            Owned by <strong>{project.OwnerName ?? 'No owner'}</strong>
            {project.StartDate && ` · started ${new Date(project.StartDate).toLocaleDateString()}`}
            {project.DueDate && ` · due ${new Date(project.DueDate).toLocaleDateString()}`}
          </div>
        </div>
        {canEdit && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setEditing(true)} style={ghostBtnSty()}><Edit2 size={12} /> Edit</button>
            {canDelete && <button onClick={deleteProject} style={ghostBtnSty()}><Trash2 size={12} /></button>}
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 20 }}>
        {/* Main */}
        <div>
          {project.Description && (
            <Card>
              <SectionLabel>Description</SectionLabel>
              <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6, color: C.text, fontSize: 14 }}>{project.Description}</div>
            </Card>
          )}

          {/* Tasks */}
          <Card>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <SectionLabel>
                Tasks · {tasks.filter(t => t.Completed).length} of {tasks.length} done
              </SectionLabel>
              <span style={{ fontSize: 12, color: C.textMuted }}>{taskProgress}%</span>
            </div>

            {tasks.length === 0 ? (
              <div style={{ color: C.textMuted, fontSize: 13, padding: 12, textAlign: 'center' }}>
                No tasks yet. Add one below.
              </div>
            ) : (
              <div>
                {tasks.map(t => (
                  <React.Fragment key={t.TaskId}>
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '8px 0', borderBottom: `1px solid ${C.border}`,
                  }}>
                    <button
                      onClick={() => canEdit && toggleTask(t)}
                      disabled={!canEdit}
                      style={{
                        background: 'none', border: 'none', padding: 0,
                        cursor: canEdit ? 'pointer' : 'default', color: t.Completed ? C.ok : C.textMuted,
                        display: 'inline-flex',
                      }}
                    >
                      {t.Completed ? <CheckCircle2 size={18} /> : <Circle size={18} />}
                    </button>
                    <span style={{
                      flex: 1, fontSize: 13,
                      color: t.Completed ? C.textMuted : C.text,
                      textDecoration: t.Completed ? 'line-through' : 'none',
                    }}>{t.Label}</span>
                    {canEdit ? (
                      <>
                        <select
                          value={t.AssignedUserId ?? ''}
                          onChange={e => updateTask(t.TaskId, { assignedUserId: e.target.value ? +e.target.value : null })}
                          style={{ fontSize: 11, padding: '2px 6px', borderRadius: 4, border: `1px solid ${C.border}`, background: C.bg, color: C.text, maxWidth: 140 }}
                          title="Assignee"
                        >
                          <option value="">Unassigned</option>
                          {assignableUsers.map(u => (
                            <option key={u.UserId} value={u.UserId}>{u.DisplayName}</option>
                          ))}
                        </select>
                        <input
                          type="date"
                          value={t.DueDate ? t.DueDate.slice(0, 10) : ''}
                          onChange={e => updateTask(t.TaskId, { dueDate: e.target.value || null })}
                          style={{ fontSize: 11, padding: '2px 6px', borderRadius: 4, border: `1px solid ${C.border}`, background: C.bg, color: C.text }}
                          title="Due date"
                        />
                      </>
                    ) : (
                      <>
                        {t.AssigneeName && (
                          <span style={{ fontSize: 11, color: C.textMuted }}>{t.AssigneeName}</span>
                        )}
                        {t.DueDate && (
                          <span style={{ fontSize: 11, color: C.textMuted }}>{new Date(t.DueDate).toLocaleDateString()}</span>
                        )}
                      </>
                    )}
                    {canEdit && (
                      <button onClick={() => setExpandedTask(expandedTask === t.TaskId ? null : t.TaskId)} title="Attachments" style={{
                        background: 'none', border: 'none', cursor: 'pointer',
                        padding: 2, color: expandedTask === t.TaskId ? C.primary : C.textMuted, display: 'inline-flex',
                      }}>
                        <Paperclip size={12} />
                      </button>
                    )}
                    {canEdit && (
                      <button onClick={() => deleteTask(t.TaskId)} style={{
                        background: 'none', border: 'none', cursor: 'pointer',
                        padding: 2, color: C.danger, display: 'inline-flex',
                      }}>
                        <Trash2 size={12} />
                      </button>
                    )}
                  </div>
                  {canEdit && expandedTask === t.TaskId && (
                    <div style={{ padding: '4px 0 12px 28px' }}>
                      <TaskAttachments taskId={t.TaskId} canEdit={canEdit} />
                    </div>
                  )}
                  </React.Fragment>
                ))}
              </div>
            )}

            {canEdit && (
              <div style={{
                marginTop: 14, padding: 12, background: C.bg, borderRadius: 6,
                display: 'flex', gap: 8,
              }}>
                <input
                  value={newTaskLabel}
                  onChange={e => setNewTaskLabel(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && addTask()}
                  placeholder="Add a task and press Enter…"
                  style={{ ...inputSty(), flex: 1 }}
                />
                <button onClick={addTask} disabled={!newTaskLabel.trim()} style={primaryBtnSty(!newTaskLabel.trim())}>
                  <Plus size={12} /> Add
                </button>
              </div>
            )}
          </Card>

          {canEdit && <ProjectNotes projectId={projectId} canEdit={canEdit} />}
        </div>

        {/* Sidebar */}
        <div>
          <Card>
            <SectionLabel>Progress</SectionLabel>
            <div style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: C.textMuted, marginBottom: 4 }}>
                <span>Tasks Complete</span>
                <span style={{ color: C.text, fontWeight: 600 }}>{taskProgress}%</span>
              </div>
              <div style={{ height: 8, background: C.bg, borderRadius: 4, overflow: 'hidden' }}>
                <div style={{
                  height: '100%', width: `${taskProgress}%`,
                  background: STATUS_COLORS[project.Status] ?? C.primary,
                  transition: 'width 0.3s',
                }} />
              </div>
            </div>
          </Card>

          <Card>
            <SectionLabel><Users size={11} style={{ verticalAlign: 'middle' }} /> Team ({team.length})</SectionLabel>
            {team.length === 0 ? (
              <div style={{ color: C.textMuted, fontSize: 12, fontStyle: 'italic' }}>No team members yet.</div>
            ) : (
              team.map(t => (
                <div key={t.UserId} style={{
                  padding: '6px 0', fontSize: 13, color: C.text,
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                }}>
                  <span>{t.DisplayName}</span>
                  <span style={{ fontSize: 11, color: C.textMuted, textTransform: 'capitalize' }}>{t.Role}</span>
                </div>
              ))
            )}
          </Card>
        </div>
      </div>

      {editing && (
        <ProjectModal
          project={project}
          users={users}
          team={team}
          onSave={async (body) => {
            try {
              await api.patch(`/api/projects/${projectId}`, body);
              setEditing(false);
              await load();
            } catch (e: any) { alert(`Failed: ${e?.message}`); }
          }}
          onClose={() => setEditing(false)}
        />
      )}
    </div>
  );
};

// ============================================================================
// Modal
// ============================================================================
const ProjectModal: React.FC<{
  project: Project | null;
  users: User[];
  team?: User[];
  onSave: (body: any) => Promise<void>;
  onClose: () => void;
}> = ({ project, users, team: initialTeam, onSave, onClose }) => {
  const [name, setName] = useState(project?.Name ?? '');
  const [description, setDescription] = useState(project?.Description ?? '');
  const [ownerUserId, setOwnerUserId] = useState<number | null>(project?.OwnerUserId ?? null);
  const [status, setStatus] = useState(project?.Status ?? 'planning');
  const [progress, setProgress] = useState(project?.Progress ?? 0);
  const [startDate, setStartDate] = useState(project?.StartDate?.slice(0, 10) ?? '');
  const [dueDate, setDueDate] = useState(project?.DueDate?.slice(0, 10) ?? '');
  const [teamIds, setTeamIds] = useState<number[]>(
    initialTeam?.map(t => t.UserId) ?? (() => {
      try { return JSON.parse(project?.TeamJson ?? '[]'); } catch { return []; }
    })()
  );
  const [saving, setSaving] = useState(false);

  const toggleTeamMember = (userId: number) => {
    setTeamIds(prev => prev.includes(userId)
      ? prev.filter(id => id !== userId)
      : [...prev, userId]);
  };

  const save = async () => {
    if (!name.trim()) { alert('Project name is required'); return; }
    setSaving(true);
    try {
      await onSave({
        name: name.trim(),
        description: description.trim() || null,
        ownerUserId,
        status,
        progress,
        startDate: startDate || null,
        dueDate: dueDate || null,
        team: teamIds,
      });
    } finally { setSaving(false); }
  };

  const eligibleUsers = users.filter(u => u.Role === 'admin' || u.Role === 'tech');

  return (
    <div style={modalBackdrop}>
      <div style={{ ...modalCard, maxWidth: 620 }}>
        <div style={modalHeader}>
          <h3 style={{ margin: 0, color: C.primary, fontSize: 16 }}>
            {project ? 'Edit Project' : 'New Project'}
          </h3>
          <button onClick={onClose} style={modalCloseBtn}><X size={16} /></button>
        </div>

        <div style={{ padding: 20, overflowY: 'auto', flex: 1 }}>
          <Field label="Project Name">
            <input value={name} onChange={e => setName(e.target.value)}
              style={inputSty()} placeholder="e.g. Windows 11 Rollout" autoFocus />
          </Field>

          <Field label="Description">
            <textarea value={description} onChange={e => setDescription(e.target.value)}
              rows={3} style={{ ...inputSty(), resize: 'vertical', fontFamily: 'inherit' }}
              placeholder="What this project will accomplish" />
          </Field>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Owner">
              <select value={ownerUserId ?? ''}
                onChange={e => setOwnerUserId(e.target.value ? parseInt(e.target.value) : null)}
                style={inputSty()}>
                <option value="">— no owner —</option>
                {eligibleUsers.map(u => <option key={u.UserId} value={u.UserId}>{u.DisplayName}</option>)}
              </select>
            </Field>
            <Field label="Status">
              <select value={status} onChange={e => setStatus(e.target.value)} style={inputSty()}>
                {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <Field label="Start Date">
              <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} style={inputSty()} />
            </Field>
            <Field label="Due Date">
              <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} style={inputSty()} />
            </Field>
            <Field label="Progress (%)">
              <input type="number" min={0} max={100} value={progress}
                onChange={e => setProgress(Math.max(0, Math.min(100, parseInt(e.target.value) || 0)))}
                style={inputSty()} />
            </Field>
          </div>

          <Field label="Team Members">
            <div style={{
              padding: 12, background: C.bg, borderRadius: 6, border: `1px solid ${C.border}`,
              maxHeight: 180, overflowY: 'auto',
            }}>
              {eligibleUsers.length === 0 ? (
                <div style={{ color: C.textMuted, fontSize: 12, fontStyle: 'italic' }}>No eligible team members.</div>
              ) : (
                eligibleUsers.map(u => (
                  <label key={u.UserId} style={{
                    display: 'flex', alignItems: 'center', gap: 8, padding: 4, cursor: 'pointer',
                    fontSize: 13,
                  }}>
                    <input type="checkbox" checked={teamIds.includes(u.UserId)}
                      onChange={() => toggleTeamMember(u.UserId)} />
                    {u.DisplayName}
                    <span style={{ marginLeft: 'auto', fontSize: 11, color: C.textMuted, textTransform: 'capitalize' }}>{u.Role}</span>
                  </label>
                ))
              )}
            </div>
          </Field>
        </div>

        <div style={modalFooter}>
          <button onClick={onClose} style={ghostBtnSty()}>Cancel</button>
          <button onClick={save} disabled={saving || !name.trim()} style={primaryBtnSty(saving || !name.trim())}>
            <Save size={12} /> {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Shared bits
// ============================================================================
const Badge: React.FC<{ children: React.ReactNode; color: string }> = ({ children, color }) => (
  <span style={{
    display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
    color, background: `${color}15`, border: `1px solid ${color}40`, textTransform: 'capitalize',
  }}>{children}</span>
);

const Card: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{
    background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8,
    padding: 18, marginBottom: 16,
  }}>{children}</div>
);

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h3 style={{
    margin: '0 0 12px', fontSize: 11, color: C.textMuted, fontWeight: 600,
    textTransform: 'uppercase', letterSpacing: '0.4px',
  }}>{children}</h3>
);

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div style={{ marginBottom: 14 }}>
    <div style={{
      fontSize: 11, fontWeight: 600, color: C.textMuted,
      marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.4px',
    }}>{label}</div>
    {children}
  </div>
);

const inputSty = (): React.CSSProperties => ({
  border: `1px solid ${C.border}`, padding: '8px 10px', borderRadius: 6,
  fontSize: 14, width: '100%', boxSizing: 'border-box', background: C.bg,
});
const primaryBtnSty = (disabled = false): React.CSSProperties => ({
  background: disabled ? '#ccc' : C.primary, color: '#fff', border: 'none',
  padding: '8px 14px', borderRadius: 6, fontSize: 13, fontWeight: 600,
  cursor: disabled ? 'not-allowed' : 'pointer',
  display: 'inline-flex', alignItems: 'center', gap: 6,
});
const ghostBtnSty = (): React.CSSProperties => ({
  background: 'transparent', color: C.text, border: `1px solid ${C.border}`,
  padding: '6px 12px', borderRadius: 6, fontSize: 12, cursor: 'pointer',
  display: 'inline-flex', alignItems: 'center', gap: 4,
});
const backBtnSty = (): React.CSSProperties => ({
  background: 'none', border: 'none', color: C.primary,
  fontSize: 13, cursor: 'pointer', display: 'inline-flex',
  alignItems: 'center', gap: 4, padding: 0, marginBottom: 12,
});
const modalBackdrop: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 100, padding: 20,
};
const modalCard: React.CSSProperties = {
  background: C.surface, borderRadius: 8, width: '100%',
  maxHeight: '90vh', display: 'flex', flexDirection: 'column',
};
const modalHeader: React.CSSProperties = {
  padding: '14px 20px', borderBottom: `1px solid ${C.border}`,
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
};
const modalFooter: React.CSSProperties = {
  padding: '12px 20px', borderTop: `1px solid ${C.border}`,
  display: 'flex', justifyContent: 'flex-end', gap: 8,
};
const modalCloseBtn: React.CSSProperties = {
  background: 'none', border: 'none', cursor: 'pointer',
  padding: 4, color: C.textMuted, display: 'inline-flex',
};
